import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { ButtonStyle } from '@constants/button/1.0';
import { combineLatest } from 'rxjs';
import { filter, take, withLatestFrom } from 'rxjs/operators';
import { ButtonComponent } from '@components/button/1.0/button.component';

const appState = { target: 'role-routing' };

@Component({
  selector: 'txp-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  host: { class: 'txp-home' },
  standalone: true,
  imports: [ButtonComponent],
})
export class HomeComponent implements OnInit {
  buttonStyle = ButtonStyle;
  user: any;
  title: string;
  isAuthenticated: boolean;

  constructor(
    private authService: AuthService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit() {
    this.authService.user$.subscribe((user) => {
      this.user = user;
    });

    this.activatedRoute.queryParams.subscribe((params) => {
      this.handleInvitation(params);
    });

    combineLatest([this.authService.isLoading$, this.authService.isAuthenticated$])
      .pipe(
        filter(([isLoading]) => !isLoading),
        take(1),
        withLatestFrom(this.authService.error$),
      )
      .subscribe(([[, isAuthenticated], sdkError]) => {
        if (sdkError) {
          this.router.navigate(['/auth-error'], {
            queryParams: { error_description: (sdkError as Error).message },
            replaceUrl: true,
          });
          return;
        }
        this.isAuthenticated = isAuthenticated;
        if (!isAuthenticated) {
          const isTimeout = this.activatedRoute?.snapshot?.queryParams?.timeout === 'true';
          if (!isTimeout) {
            this.login();
          }
        }
      });
  }

  handleInvitation({ invitation, organization }: Record<string, string>) {
    if (!invitation) return;
    this.authService.loginWithRedirect({
      appState,
      authorizationParams: {
        invitation,
        organization,
      },
    });
  }

  async login() {
    if (this.activatedRoute?.snapshot?.queryParams?.invitation) {
      this.handleInvitation(this.activatedRoute?.snapshot?.queryParams);
    } else {
      const isTimeout = this.activatedRoute?.snapshot?.queryParams?.timeout === 'true';
      const params: any = { appState };
      if (isTimeout) {
        params.authorizationParams = {
          prompt: 'login',
        };
      }
      this.authService.loginWithRedirect(params);
    }
  }

  logout() {
    // Save all user-specific orders dashboard filter keys/values
    const filterKeys = [
      'ordersDashboardSortNewestFirst_',
      'ordersDashboardActiveTimerFilter_',
      'ordersDashboardSelectedServiceType_',
      'ordersDashboardStoreFilter_',
      'ordersDashboardNext5Minutes_',
      'ordersDashboardSearchText_',
      'ordersDashboardStoreSearchText_',
      'storeMenuShowImages',
    ];
    const filterValues: [string, string][] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && filterKeys.some((prefix) => key.startsWith(prefix))) {
        const value = localStorage.getItem(key);
        if (value !== null) {
          filterValues.push([key, value]);
        }
      }
    }

    this.authService.logout({
      logoutParams: {
        federated: true,
        returnTo: window.location.origin,
      },
    });

    window.sessionStorage.clear();
    localStorage.clear();

    // Restore all user-specific orders dashboard filter keys/values
    filterValues.forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
  }
}
